/**
 * The Migrate area's pure rules: what the review forms start with.
 *
 * Kept import-free (types only) so `node --test` can load it straight from
 * source. The shapes below are structural twins of the generated
 * `LegacySummary` / `MigrateImportRequest`, narrowed to what these read, so the
 * helpers stay testable without the SDK.
 *
 * Everything here is a *suggestion* for an editable form: the reviewer changes
 * any of it, and the server re-validates all of it at import.
 */

/** The three urgencies an incident is filed with. */
export type UrgencyCode = "HIGH" | "MEDIUM" | "LOW";

/** Incident (IN) or service request (RO). */
export type MigrationKindCode = "IN" | "RO";

/** `TicketCreateRequest`'s bounds, mirrored so a prefill never fails validation. */
export const SUBJECT_MIN_LENGTH = 3;
export const SUBJECT_MAX_LENGTH = 255;
export const DESCRIPTION_MAX_LENGTH = 4000;

export interface TaxonomyEntry {
  service: string;
  categories: string[];
}

export interface SummaryLike {
  legacy_number: string;
  subject: string;
  description: string;
  priority?: string | null;
  suggestion: {
    kind?: MigrationKindCode | null;
    service?: string | null;
    category?: string | null;
    offering_id?: string | null;
  };
  raised_by: {
    name: string;
    employee?: { email: string; display_name: string; rec_id?: string | null } | null;
  };
  taxonomy?: TaxonomyEntry[];
}

/** What the reviewer settles on in the Summary step. */
export interface Classification {
  kind: MigrationKindCode;
  service: string | null;
  category: string | null;
  offeringId: string | null;
}

/** Who the ITSM ticket is for. */
export interface Customer {
  email: string;
  displayName: string;
  recId?: string | null;
}

export interface IncidentDraft {
  subject: string;
  description: string;
  urgency: UrgencyCode;
}

/**
 * The legacy app's priority text → the incident's urgency.
 *
 * The legacy values are free-ish display text ("High", "1 - High", "Urgent",
 * "Low"); anything unrecognised is Medium, the service desk's own default.
 */
export function legacyPriorityToUrgency(priority: string | null | undefined): UrgencyCode {
  const text = (priority ?? "").toLowerCase();
  if (/\b(high|urgent|critical)\b/.test(text)) {
    return "HIGH";
  }
  if (/\blow\b/.test(text)) {
    return "LOW";
  }
  return "MEDIUM";
}

/** `text` cut to `max` characters, trailing space trimmed. */
export function clip(text: string, max: number): string {
  const trimmed = text.trim();
  return trimmed.length <= max ? trimmed : trimmed.slice(0, max).trimEnd();
}

/** The categories a service offers, or none for an unknown service. */
export function categoriesFor(
  taxonomy: readonly TaxonomyEntry[] | undefined,
  service: string | null,
): string[] {
  if (service === null) {
    return [];
  }
  return (taxonomy ?? []).find((entry) => entry.service === service)?.categories ?? [];
}

/**
 * The classification the Summary step opens on: the suggestion, where it fits.
 *
 * An IN suggestion keeps its Service/Category only as a pair that is on the
 * taxonomy; an RO keeps its offering only when it resolved to a published id.
 * No suggestion at all opens as an incident with nothing chosen.
 */
export function initialClassification(summary: SummaryLike): Classification {
  const suggestion = summary.suggestion;
  const kind: MigrationKindCode = suggestion.kind ?? "IN";
  const service = suggestion.service ?? null;
  const category = suggestion.category ?? null;
  const pairIsValid =
    service !== null &&
    category !== null &&
    categoriesFor(summary.taxonomy, service).includes(category);
  return {
    kind,
    service: kind === "IN" && pairIsValid ? service : null,
    category: kind === "IN" && pairIsValid ? category : null,
    offeringId: kind === "RO" ? (suggestion.offering_id ?? null) : null,
  };
}

/** Whether the Summary step's OK may proceed. */
export function isClassificationComplete(classification: Classification): boolean {
  if (classification.kind === "IN") {
    return classification.service !== null && classification.category !== null;
  }
  return classification.offeringId !== null;
}

/** The raised-by person, when Ivanti knows them — the default customer. */
export function defaultCustomer(summary: SummaryLike): Customer | null {
  const employee = summary.raised_by.employee;
  if (employee === null || employee === undefined || employee.email === "") {
    return null;
  }
  return {
    email: employee.email,
    displayName: employee.display_name,
    recId: employee.rec_id ?? null,
  };
}

/**
 * A subject that satisfies the create contract.
 *
 * The legacy subject, cut to 255; a blank or too-short one gets the legacy
 * number in front so the ITSM ticket is still findable.
 */
export function legacySubject(summary: SummaryLike): string {
  const subject = clip(summary.subject, SUBJECT_MAX_LENGTH);
  if (subject.length >= SUBJECT_MIN_LENGTH) {
    return subject;
  }
  return clip(`${summary.legacy_number} ${subject}`, SUBJECT_MAX_LENGTH);
}

/** The legacy description (or the subject when it has none), cut to the limit. */
export function legacyDescription(summary: SummaryLike): string {
  const text = summary.description.trim() === "" ? summary.subject : summary.description;
  return clip(text, DESCRIPTION_MAX_LENGTH);
}

/** The incident form's starting values. */
export function buildIncidentDraft(summary: SummaryLike): IncidentDraft {
  return {
    subject: legacySubject(summary),
    description: legacyDescription(summary),
    urgency: legacyPriorityToUrgency(summary.priority),
  };
}

export interface ImportAnswer {
  parameter_id: string;
  value?: string | null;
  value_ids?: string[];
  attachment_ids?: string[];
}

export interface ImportBodyInput {
  legacyNumber: string;
  classification: Classification;
  customer: Customer;
  subject: string;
  description: string;
  language: string;
  urgency?: UrgencyCode;
  answers?: ImportAnswer[];
  attachmentIds: string[];
}

/** The `importLegacyTicket` body, shaped for the kind being filed. */
export function buildImportBody(input: ImportBodyInput) {
  const base = {
    legacy_number: input.legacyNumber,
    kind: input.classification.kind,
    customer_email: input.customer.email,
    subject: clip(input.subject, SUBJECT_MAX_LENGTH),
    description: clip(input.description, DESCRIPTION_MAX_LENGTH),
    body_lang: input.language,
    attachment_ids: [...new Set(input.attachmentIds)],
  };
  if (input.classification.kind === "IN") {
    return {
      ...base,
      service_id: input.classification.service,
      category_id: input.classification.category,
      urgency_code: input.urgency ?? "MEDIUM",
      answers: [],
    };
  }
  return {
    ...base,
    offering_id: input.classification.offeringId,
    answers: input.answers ?? [],
  };
}

/** `" s-00004954 "` → `"S-00004954"`. */
export function normaliseLegacyNumber(text: string): string {
  return text.trim().toUpperCase();
}

/** The legacy app's ticket number shape: letters, dash, digits (`S-00004954`). */
export function isLegacyNumber(text: string): boolean {
  return /^[A-Z]{1,4}-\d{1,12}$/.test(text);
}

/** The lookup's refusal when the legacy CRM cannot be read as this migrator. */
export const LEGACY_NOT_CONNECTED = "LEGACY_NOT_CONNECTED";

/** The parts of a `LegacySummary` the attachments overview reads. */
export interface AttachmentsLike {
  note_files?: { source: string }[];
  documents: { available: boolean; folder_url?: string | null; items?: unknown[] };
}

/** What the import will bring across, counted for the Summary step. */
export interface AttachmentsOverview {
  /** Screenshots pasted into posts. */
  images: number;
  /** Files attached to document notes. */
  files: number;
  /** SharePoint documents the app can fetch itself. */
  documents: number;
  /** A SharePoint folder exists but cannot be read: the reviewer adds its files by hand. */
  documentsManual: boolean;
}

/** Count the legacy ticket's pasted images, note files and SharePoint documents. */
export function attachmentsOverview(summary: AttachmentsLike): AttachmentsOverview {
  const notes = summary.note_files ?? [];
  const { documents } = summary;
  return {
    images: notes.filter((file) => file.source === "note image").length,
    files: notes.filter((file) => file.source !== "note image").length,
    documents: documents.available ? (documents.items ?? []).length : 0,
    documentsManual: !documents.available && (documents.folder_url ?? "") !== "",
  };
}

/**
 * Which hint the offering picker needs, if any.
 *
 * An empty catalog means it could not be read for this account — not that the
 * suggested offering is unpublished, which is only true when the catalog did
 * load and lacks it.
 */
export function offeringHint(
  offeringCount: number,
  suggestion: { offering_name?: string | null; offering_id?: string | null },
): "catalogUnavailable" | "notPublished" | null {
  if (offeringCount === 0) {
    return "catalogUnavailable";
  }
  if (suggestion.offering_name && suggestion.offering_id == null) {
    return "notPublished";
  }
  return null;
}

export interface UploadFilesLike {
  note_files?: readonly { allowed?: boolean | null }[] | null;
  documents: { available: boolean; items?: readonly { allowed?: boolean | null }[] | null };
}

/**
 * Whether an offering's required upload field is waived for this migration.
 *
 * Only a file that will really be attached counts: one the migrator dropped
 * in, or a legacy file the attachment policy allows (`allowed !== false`).
 * The server decides again at import from what actually downloaded; when it
 * still asks for the file (`serverWantsUpload`), the field stays required.
 */
export function isUploadWaived(
  summary: UploadFilesLike,
  droppedIds: readonly string[],
  serverWantsUpload: boolean,
): boolean {
  if (serverWantsUpload) {
    return false;
  }
  const allowed = (items: readonly { allowed?: boolean | null }[] | null | undefined) =>
    (items ?? []).some((file) => file.allowed !== false);
  return (
    droppedIds.length > 0 ||
    allowed(summary.note_files) ||
    (summary.documents.available && allowed(summary.documents.items))
  );
}
